import { useEffect, useState } from 'react'
import { fetchOperatorMonthly, type OperatorMonthly } from '../api/operatorMonthly'
import { Card, CardContent, CardHeader, DataTable, Select } from './ui'

const monthName = (m: number): string =>
  new Intl.DateTimeFormat('it-IT', { month: 'long' }).format(new Date(2024, m - 1, 1))
const day = (iso: string): string => {
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

/**
 * "Contratti per operatore (mese gara)": per ogni operatore i contratti del mese scelto per stato, con il confronto sul mese
 * precedente. Il mese gara parte dal giorno configurato sull'operatore (es. Ermes dal 21 del mese prima al 20); le date usate
 * sono mostrate sotto il titolo. Conta signed_at (in mancanza created_at); i dati sono quelli visibili all'utente.
 */
export function OperatorMonthlySection() {
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [data, setData] = useState<OperatorMonthly | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let alive = true
    setData(null)
    setError(false)
    fetchOperatorMonthly(year, month)
      .then(d => { if (alive) setData(d) })
      .catch(() => { if (alive) setError(true) })
    return () => { alive = false }
  }, [year, month])

  const years = Array.from({ length: 4 }, (_, i) => now.getFullYear() - i)
  const ranges = data
    ? Array.from(new Set(data.items.map(i => `${i.from}|${i.to}`)))
        .map(r => r.split('|'))
    : []

  return (
    <Card variant="outlined" padding="none">
      <CardHeader
        title="Contratti per operatore (mese gara)"
        subtitle={ranges.length === 0 ? 'Data di firma; il mese gara parte dal giorno impostato su ciascun operatore' : undefined}
        actions={(
          <div className="d-flex gap-2">
            <Select label="Mese" value={month} onChange={e => setMonth(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{monthName(m)}</option>)}
            </Select>
            <Select label="Anno" value={year} onChange={e => setYear(Number(e.target.value))}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </Select>
          </div>
        )}
      />
      <CardContent>
        {data && ranges.length > 0 && (
          <p className="text-muted">
            {ranges.map(([from, to]) => {
              const names = data.items.filter(i => i.from === from && i.to === to).map(i => i.operatorName)
              return <span key={`${from}${to}`} className="d-block">
                {day(from)} - {day(to)}: {names.length > 6 ? `${names.length} operatori` : names.join(', ')}
              </span>
            })}
          </p>
        )}
        {error && <p className="text-muted">Impossibile caricare i contratti per operatore.</p>}
        {!data && !error && <p className="text-muted">Caricamento...</p>}
        {data && data.items.length === 0 && <p className="text-muted">Nessun contratto nel periodo.</p>}
        {data && data.items.length > 0 && (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Operatore</th>
                <th className="text-end">Totale</th>
                <th className="text-end">Attivati</th>
                <th className="text-end">In lavorazione</th>
                <th className="text-end">KO / rifiutati</th>
                <th className="text-end">Stornati</th>
                <th className="text-end">Mese prec.</th>
                <th className="text-end">Var.</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(i => (
                <tr key={i.operatorId}>
                  <td>{i.operatorName}</td>
                  <td className="text-end md-num fw-bold">{i.current.total}</td>
                  <td className="text-end md-num">{i.current.activated}</td>
                  <td className="text-end md-num">{i.current.inProgress}</td>
                  <td className="text-end md-num">{i.current.rejected}</td>
                  <td className="text-end md-num">{i.current.storno}</td>
                  <td className="text-end md-num">{i.previous.total}</td>
                  <td className="text-end md-num">{i.delta > 0 ? `+${i.delta}` : i.delta}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </CardContent>
    </Card>
  )
}
